import { useContext } from 'react'
import { Badge } from 'antd'

import { formatDate, formatNb } from '../utils/utils'
import { getGiftCards } from '../network/GiftCards'

import PageWrap from '../components/layout/PageWrap'
import TableBlock from '../components/tables/TableBlock'
import DynamicStrings from '../components/DynamicStrings'
import { AppContext } from 'src/context/AppContext'

const GiftCards = ({ history }) => {
    const { user } = useContext(AppContext)

    const commuFilter = (key) =>
        user.role === 'admin' || user.role === 'super-admin'
            ? {}
            : { [key]: { $in: user.communities } }

    return (
        <PageWrap title="MENU_GIFT_CARDS">
            <TableBlock
                columns={columns}
                filters={filters}
                name="getGiftCards"
                request={getGiftCards}
                pageSize={80}
                otherFilters={{
                    ...commuFilter('communities'),
                }}
                onRow={(record) => ({
                    onClick: () => {
                        if (record.status !== 'used') {
                            history.push(`/gift-cards/${record.id}`)
                        }
                    },
                    style:
                        record.status === 'used'
                            ? { cursor: 'default' }
                            : { cursor: 'pointer' },
                })}
            />
        </PageWrap>
    )
}

const filters = (history) => ({
    fields: [
        {
            type: 'text',
            label: 'GIFT_CARDS_GIFT_CARD_ID',
            key: 'giftCardID',
        },
        {
            type: 'text',
            label: 'CUSTOMERS_CUSTOMER_NAME',
            key: 'customer.name',
            noRegex: true,
        },
        {
            type: 'select',
            label: 'GIFT_CARDS_TYPE',
            default: '',
            key: 'type',
            options: [
                { key: 'purchase', label: 'purchase' },
                { key: 'gift', label: 'gift' },
                { key: 'manual', label: 'manual' },
                { key: 'other', label: 'other' },
            ],
        },
        {
            type: 'select',
            label: 'OFFERS_STATUS',
            default: '',
            key: 'status',
            options: [
                { key: 'validated', label: 'validated' },
                { key: 'used', label: 'used' },
            ],
        },
        {
            type: 'daterange',
            label: 'ANALYTICS_DATES',
            key: ['dateStart', 'dateEnd'],
        },
    ],
    actions: [
        {
            label: 'GIFT_CARDS_CREATE',
            isActive: true,
            action: () => history.push('/gift-cards/create'),
        },
    ],
})

const columns = () => [
    {
        title: <DynamicStrings id="GIFT_CARDS_GIFT_CARD_ID" />,
        dataIndex: 'giftCardID',
        key: 'giftCardID',
    },
    {
        title: <DynamicStrings id="CUSTOMERS_CUSTOMER_NAME" />,
        dataIndex: 'customer',
        render: (customer) => customer?.name,
        key: 'customer',
        sorter: false,
    },
    {
        title: <DynamicStrings id="GIFT_CARDS_TYPE" />,
        dataIndex: 'type',
        render: (type) => <DynamicStrings id={type} />,
        key: 'type',
        sorter: true,
    },
    {
        title: <DynamicStrings id="GIFT_CARDS_GRANTED_BY" />,
        dataIndex: 'grantedBy',
        render: (grantedBy) => grantedBy?.name,
        key: 'grantedBy',
        sorter: false,
    },
    {
        title: <DynamicStrings id="GIFT_CARDS_OFFERED_BY" />,
        dataIndex: 'offeredBy',
        render: (offeredBy) => offeredBy?.name,
        key: 'offeredBy',
        sorter: false,
    },
    {
        title: <DynamicStrings id="OFFER_LIVE_TOTAL" suffix="(¥)" />,
        dataIndex: 'amount',
        render: (amount) => formatNb(amount),
        key: 'amount',
        sorter: true,
    },
    {
        title: <DynamicStrings id="ROLES_COMMUNITY" />,
        dataIndex: 'communities',
        key: 'communities',
        render: (communities) => (
            <span>
                {communities?.map((com, i) => (
                    <span key={com.id}>
                        <DynamicStrings id={com.name} />
                        {i < communities.length - 1 && ', '}
                    </span>
                ))}
            </span>
        ),
        sorter: false,
    },
    {
        title: <DynamicStrings id="ZIKO_SPECIALS_EXPIRATION_DATE" />,
        dataIndex: 'expirationDate',
        render: (date) => (date ? formatDate(date) : null),
        key: 'expirationDate',
        sorter: true,
    },
    {
        title: <DynamicStrings id="GIFT_CARDS_USED_AT" />,
        dataIndex: 'usedAt',
        render: (date) => (date ? formatDate(date) : null),
        key: 'usedAt',
        sorter: true,
    },
    {
        title: <DynamicStrings id="GIFT_CARDS_ORDER_USED_ON" />,
        dataIndex: 'orderUsedOn',
        render: (order) => order?.orderID,
        key: 'orderUsedOn',
        sorter: false,
    },
    {
        title: <DynamicStrings id="OFFERS_STATUS" />,
        dataIndex: 'status',
        render: (status) => {
            const color = {
                validated: 'green',
                used: 'blue',
            }

            return (
                <Badge
                    color={color[status]}
                    text={<DynamicStrings id={status} />}
                />
            )
        },
        key: 'status',
        sorter: true,
    },
    {
        title: <DynamicStrings id="ZIKO_SPECIALS_CREATION_DATE" />,
        dataIndex: 'createdAt',
        render: (date) => (date ? formatDate(date) : null),
        key: 'createdAt',
        sorter: true,
    },
]

export default GiftCards
